import { useCallback, useEffect, useState } from 'react';
// Shared 30 second OTP cooldown for ABDM OTP generation APIs
// (aadhaar/otp, abha/otp, mobile/otp). Cooldown is keyed by the
// identifier (aadhaar/abha/mobile) and persisted in localStorage so
// re-opening the popup and retrying the same value still respects it.
import { otpCooldownTime } from '@constants/appConstants';
import config from '@config/config';
const configuredOtpCooldownTime = Number(config?.otpCooldownTime) || otpCooldownTime;

const STORAGE_KEY = 'otpCooldownMap';

type CooldownMap = Record<string, number>; // identifier -> expiry time (ms)

const readMap = (): CooldownMap => {
  try {
    const map: CooldownMap = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const now = Date.now();
    let changed = false;
    for (const k of Object.keys(map)) {
      if (map[k] <= now) {
        delete map[k];
        changed = true;
      }
    }
    if (changed) localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    return map;
  } catch {
    return {};
  }
};
const writeMap = (map: CooldownMap) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
};

const secondsRemaining = (expiry?: number): number =>
  expiry ? Math.max(0, Math.ceil((expiry - Date.now()) / 1000)) : 0;

// True when the API response/error indicates the 429 "wait 30 seconds" case.
export const isOtpRateLimited = (message?: string): boolean =>
  !!message && (message.includes('429') || message.includes('30 seconds'));

// Detects the 429 case from either a thrown axios error or a decoded
// response object. Handles both shapes:
//  - error.response.data.messages[0].messageDetails = "...429"
//  - decoded response body { data: null, messages: [{ messageDetails }] }
export const isOtpRateLimitedError = (error: any): boolean => {
  if (!error) {
    return false;
  }
  if (error?.response?.status === 429 || error?.status === 429) {
    return true;
  }
  const payload = error?.response?.data ?? error;
  if (isOtpRateLimited(String(payload?.message ?? ''))) {
    return true;
  }
  const messages = Array.isArray(payload?.messages) ? payload.messages : [];
  return messages.some((msg: any) =>
    isOtpRateLimited(
      `${msg?.messageDetails ?? ''} ${msg?.messageCode ?? ''} ${msg?.messageDescription ?? ''}`,
    ),
  );
};

// Starts the cooldown for an identifier without needing the React hook.
// Useful for resend/secondary screens that only need to trigger it.
export const startOtpCooldown = (identifier?: string) => {
  const key = (identifier || '').trim();
  if (!key) {
    console.log('startOtpCooldown called without a valid identifier');
    return;
  }
  const map = readMap();
  map[key] = Date.now() + configuredOtpCooldownTime * 1000;
  writeMap(map);
};

export const useOtpCooldown = (identifier?: string) => {
  const key = (identifier || '').trim();
  const [secondsLeft, setSecondsLeft] = useState<number>(() =>
    secondsRemaining(readMap()[key]),
  );

  // Poll once per second so cooldowns started here or via the standalone
  // startOtpCooldown (from other flows) are reflected, and so it ticks down.
  useEffect(() => {
    setSecondsLeft(secondsRemaining(readMap()[key]));
    const timer = setInterval(() => {
      setSecondsLeft(secondsRemaining(readMap()[key]));
    }, 1000);
    return () => clearInterval(timer);
  }, [key]);

  const startCooldown = useCallback(() => {
    if (!key) {
      return;
    }
    startOtpCooldown(key);
    setSecondsLeft(configuredOtpCooldownTime);
  }, [key]);

  return { secondsLeft, isCoolingDown: secondsLeft > 0, startCooldown };
};
